/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
/* eslint-disable spire/fenced-imports */
import FieldsEditor from "@eisinc/App/ItemEditor/FieldsEditor";
import { loadSelectBrands, loadSelectedBrands } from "@eisinc/App/PageEditor/PageEditorActionCreators";
import { CustomEditorTemplateProps, CustomSelectBrandsFieldDefinition } from "@eisinc/CustomFieldDefinition";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { ListFieldDefinition } from "@insite/client-framework/Types/FieldDefinition";
import DynamicDropdown from "@insite/mobius/DynamicDropdown";
import StandardControl from "@insite/shell-public/Components/StandardControl";
import { SelectBrandModel } from "@insite/shell/Store/PageEditor/PageEditorState";
import ShellState from "@insite/shell/Store/ShellState";
import debounce from "lodash/debounce";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";

interface State {
    selectBrands: SelectBrandModel[];
    isFocused: boolean;
    loaded?: boolean;
}

type OwnProps = CustomEditorTemplateProps<string[], CustomSelectBrandsFieldDefinition>;

const mapStateToProps = (state: ShellState, ownProps: OwnProps) => {
    const selectBrands = state.pageEditor.selectBrandsState?.selectBrands || [];
    const selectedBrands = state.pageEditor.selectBrandsState?.selectedBrands || [];

    return {
        selectBrands,
        selectedBrands,
    };
};

const mapDispatchToProps = {
    loadSelectBrands,
    loadSelectedBrands,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

class CustomSelectBrandsField extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);

        this.state = {
            selectBrands: [],
            isFocused: false,
        };
    }

    UNSAFE_componentWillMount(): void {
        this.loadSelectBrands(this.props.fieldValue);
        this.loadSelectedBrands(this.props.fieldValue);
    }

    private loadSelectBrands(brands: string[], filter?: string) {
        this.props.loadSelectBrands();
    }

    private loadSelectedBrands(brands: string[]) {
        this.props.loadSelectedBrands();
    }

    componentDidUpdate(prevProps: Props) {
        if (prevProps.selectBrands !== this.props.selectBrands || prevProps.fieldValue !== this.props.fieldValue) {
            // TODO ISC-12679 - Redesign so that this can be moved to a field of the class rather than state.

            this.setState({
                selectBrands: this.props.selectBrands?.filter(brand => !this.props.fieldValue.includes(brand.id)) || [],
                loaded: !!this.props.selectBrands,
            });
        }
    }

    onChange = (value?: string) => {
        if (!value) {
            return;
        }
        const updateFieldValue = [...this.props.fieldValue, value];
        this.props.updateField(this.props.fieldDefinition.name, updateFieldValue);
        this.loadSelectBrands(updateFieldValue);
        this.loadSelectedBrands(updateFieldValue);
    };

    updateBrandListFromListField = (_: string, value: readonly HasFields[]) => {
        this.props.updateField(
            this.props.fieldDefinition.name,
            value.map(valueFields => valueFields.fields.id),
        );
    };

    render() {
        const { fieldValue, selectedBrands } = this.props;
        const { selectBrands, loaded } = this.state;
        const listFieldDefinition: ListFieldDefinition = {
            name: "selectBrandsListField",
            fieldDefinitions: [],
            defaultValue: [],
            editorTemplate: "ListField",
            fieldType: "General",
            getDisplay: (item: HasFields, _) => {
                const brand = selectedBrands?.filter(brand => brand.id === item.fields.id)[0];
                return brand?.name ?? item.fields.id;
            },
            hideEdit: true,
            hideAdd: true,
            tab: {
                displayName: translate("Basic"),
                sortOrder: 0,
            },
        };
        const listFieldItem = {
            fields: { selectBrandsListField: [...fieldValue.map(brandId => ({ fields: { id: brandId } }))] },
        };
        const options = selectBrands.map(option => ({ optionText: option.name, optionValue: option.id }));
        const loadSelectBrandsDebounce = debounce((value: string) => {
            this.loadSelectBrands(this.props.fieldValue, value);
        }, 100);
        const onInputChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
            loadSelectBrandsDebounce(event.target.value);
        };

        return (
            <StandardControl fieldDefinition={this.props.fieldDefinition as any}>
                {loaded && (
                    <DynamicDropdown
                        uid={this.props.fieldDefinition.name}
                        onSelectionChange={this.onChange}
                        options={options}
                        onInputChange={onInputChanged}
                        placeholder={translate("Select a Brand")}
                    />
                )}

                <FieldsEditor
                    fieldDefinitions={[listFieldDefinition] as any}
                    item={listFieldItem}
                    updateField={this.updateBrandListFromListField}
                    registerHasValidationErrors={() => false}
                    updateHasValidationErrors={() => false}
                />
            </StandardControl>
        );
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(CustomSelectBrandsField);
